import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { FeatureBlockContent } from '~/content/types';
import { Section } from '~/components/layout';
import { Grid, Icon, SmartImage } from '~/components/ui';
import { cn } from '~/lib/cn';

/**
 * Two-column block: image on one side, titled icon-row list on the other
 * (Figma Receive / Transfer / Convert pattern).
 */
export function FeatureBlock({
  content,
  imagePosition = 'left',
  sectionClassName,
  imageOverlay,
  ctaSlot,
}: {
  content: FeatureBlockContent;
  imagePosition?: 'left' | 'right';
  sectionClassName?: string;
  /** Optional card/element centred over the image (e.g. the Transfer selector). */
  imageOverlay?: ReactNode;
  /** Optional custom CTA rendered below the rows (e.g. the Download QR), replacing any text link. */
  ctaSlot?: ReactNode;
}) {
  return (
    <Section className={sectionClassName}>
      {/* On the 9-col grid: image spans 4, text spans 4, with an empty column
          between them for breathing room. Full width + stacked on mobile. */}
      <Grid className="items-center gap-y-10">
        <div
          className={cn(
            'relative col-span-9 overflow-hidden lg:col-span-4 lg:row-start-1',
            imagePosition === 'right' ? 'lg:col-start-6' : 'lg:col-start-1',
          )}>
          <SmartImage
            image={content.image}
            className="aspect-square w-full lg:aspect-auto lg:h-[720px]"
          />
          {imageOverlay && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              {imageOverlay}
            </div>
          )}
        </div>

        <div
          className={cn(
            'col-span-9 lg:col-span-4 lg:row-start-1',
            imagePosition === 'right' ? 'lg:col-start-1' : 'lg:col-start-6',
          )}>
          <div className="border-b border-black-6 pb-6">
            <h2 className="text-h4 font-semibold tracking-[-0.02em] whitespace-pre-line md:text-h3">
              {content.title}
            </h2>
          </div>
          <ul className="mt-4">
            {content.rows.map((row, i) => (
              <li key={row.title} className="flex gap-5">
                {/* Icon chip nudged down to line up with the padded text block. */}
                <span className="mt-8 flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-50 text-ink">
                  {row.logo ? (
                    <img
                      src={row.logo}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="size-6 object-contain"
                    />
                  ) : (
                    <Icon
                      name={row.icon}
                      className={row.icon.startsWith('flag-') ? 'size-8' : 'size-6'}
                    />
                  )}
                </span>
                {/* Divider lives on the text column so it starts aligned with the text, not under the icon. */}
                <div className={cn('flex-1', i > 0 && 'border-t border-black-6')}>
                  {/* 32px top/bottom padding → 32px from content to each separator; 20px between title and paragraph. */}
                  <div className="flex flex-col gap-5 py-8">
                    <h3 className="text-body font-semibold">{row.title}</h3>
                    <p className="text-body text-grey-900">{row.description}</p>
                    {row.link && (
                      <Link
                        to={row.link.href}
                        className="text-body-m font-medium text-red-500 hover:text-red-700">
                        {row.link.label}
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {ctaSlot && <div className="mt-8">{ctaSlot}</div>}
        </div>
      </Grid>
    </Section>
  );
}
